<template>
  <div class="d-flex fl-ev">
    <SButton
      :variant="isSelected ? 'default' : 'outline'"
      size="sm"
      class="cursor-pointer disabled:cursor-not-allowed"
      data-cy="customer-select-button"
      @click="onSelect"
    >
      {{ isSelected ? $t("button.unselect") : $t("button.select") }}
    </SButton>

    <div
      class="d-flex fl-j-c fl-col cursor-pointer"
      data-cy="customer-edit-button"
      @click="onEdit"
    >
      <SquarePenIcon class="is-size-4 size-6!" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { SButton } from "@smeetz/ui-core";
import { SquarePenIcon } from "@smeetz/ui-icons";

import CustomersModule from "@/store/modules/customers";
import BookingModule from "@/store/modules/booking";

import { hideDialog, toggleModalVisible } from "@/utils/vue-helpers/modal";
import { ModalStatus } from "@/constants/modal";

type Props = {
  row: Record<string, unknown>;
};

const props = defineProps<Props>();

/**
 * Whether the customer in this row is the currently-selected customer for the
 * active booking.
 *
 * @returns {boolean}
 */
const isSelected = computed((): boolean => {
  return CustomersModule.selectedCustomers.some(
    (c: { id: number | string; bookingUuid: string }) => {
      return (
        String(c.id) === String(props.row.id) &&
        c.bookingUuid === BookingModule.selectedBookingUUID
      );
    }
  );
});

/**
 * Pick the customer payload fields used by setSelectedCustomers /
 * setEditedCustomers. Mirrors the legacy CustomersTable handlers: same 16
 * fields, all read off camelCase modelkit getters.
 *
 * @returns {Record<string, unknown>}
 */
function pickCustomerFields(): Record<string, unknown> {
  const r = props.row;

  return {
    id: r.id,
    email: r.email,
    fullName: r.fullName,
    firstName: r.firstName,
    lastName: r.lastName,
    phone: r.phone,
    country: r.country,
    city: r.city,
    zipCode: r.zipCode,
    address: r.address,
    birthDate: r.birthDate,
    emailMarketingOrganiser: r.emailMarketingOrganiser,
    title: r.title,
    allergies: r.allergies,
    language: r.language,
    company: r.company,
  };
}

/**
 * Toggle selected state. The legacy "one customer per booking" rule is
 * preserved: we replace the whole `selectedCustomers` array on select and
 * empty it on unselect.
 *
 * @returns {boolean} Sentinel.
 */
function onSelect(): boolean {
  if (isSelected.value) {
    CustomersModule.setSelectedCustomers([]);
  } else {
    const payload = pickCustomerFields();

    payload.bookingUuid = BookingModule.selectedBookingUUID;
    payload.checkOrder = true;
    CustomersModule.setSelectedCustomers([payload]);
  }

  hideDialog();

  return true;
}

/**
 * Open the CustomerEdit modal pre-filled with this customer.
 *
 * @returns {boolean} Sentinel.
 */
function onEdit(): boolean {
  const payload = pickCustomerFields();

  CustomersModule.setEditedCustomers([payload]);
  toggleModalVisible(ModalStatus.EditCustomer);

  return true;
}
</script>
